<template>
  <subscription-guard>
    <div class="ugc-page">

      <!-- Header -->
      <div class="ugc-header">
        <h1 class="ugc-title">UGC <span class="ugc-beta">Beta</span></h1>
        <p class="ugc-subtitle">User-generated content you can license and reuse in ads.</p>
      </div>

      <!-- FAQ accordions -->
      <div class="ugc-faq-grid">
        <div v-for="(faq, index) in faqs" :key="faq.title" class="ugc-faq-card">
          <button class="ugc-faq-header" @click="toggleFaq(index)">
            <span>{{ faq.title }}</span>
            <i class="fal" :class="faq.open ? 'fa-chevron-up' : 'fa-chevron-down'"></i>
          </button>
          <div v-if="faq.open" class="ugc-faq-body" v-html="faq.content"></div>
        </div>
      </div>

      <!-- Main card -->
      <div class="ugc-main-card">

        <!-- LIBRARY SECTION HEADER -->
        <div class="ugc-section-header">
          <div class="ugc-section-header__left">
            <span class="ugc-section-title">Library</span>
            <span v-if="filteredAssets.length" class="ugc-section-count">{{ filteredAssets.length }}</span>
          </div>
          <div class="ugc-section-header__right">
            <div class="ugc-search-wrap">
              <i class="fal fa-search ugc-search-icon"></i>
              <input v-model="search" placeholder="Search filename..." class="ugc-search-input" />
            </div>
            <select v-model="filterStatus" class="ugc-select">
              <option v-for="opt in filterOptions" :key="opt.value" :value="opt.value">{{ opt.text }}</option>
            </select>
            <button class="ugc-refresh-btn" :class="{ 'ugc-refresh-btn--loading': loading }" @click="loadData">
              <i class="fal fa-sync-alt"></i> Refresh
            </button>
          </div>
        </div>

        <div v-if="error" class="ugc-error">{{ error }}</div>

        <!-- LIBRARY GRID -->
        <div class="ugc-section-body">
          <div v-if="filteredAssets.length" class="ugc-grid">
            <div v-for="asset in filteredAssets" :key="asset.id" class="ugc-asset-card">
              <div class="ugc-asset-thumb" :style="!asset.thumbnail_url ? assetPlaceholderStyle(asset.id) : null">
                <img v-if="asset.thumbnail_url" :src="asset.thumbnail_url" class="ugc-asset-thumb-img" alt="" />
                <i
                  v-if="!asset.thumbnail_url"
                  class="fal"
                  :class="asset.mime_type && asset.mime_type.startsWith('video') ? 'fa-play-circle' : 'fa-image'"
                ></i>
                <i
                  v-else-if="asset.mime_type && asset.mime_type.startsWith('video')"
                  class="fal fa-play-circle ugc-asset-play-icon"
                ></i>
                <span class="ugc-asset-status-badge" :class="'ugc-status--' + getStateColor(asset)">{{ getStateLabel(asset) }}</span>
              </div>
              <div class="ugc-asset-body">
                <div class="ugc-asset-filename" :title="asset.original_filename">{{ asset.original_filename }}</div>
                <div class="ugc-asset-meta">
                  <span>{{ getInfluencerName(asset) }}</span>
                  <span class="ugc-asset-price">{{ formatMinorAmount(getDisplayedPrice(asset.id), getDisplayedCurrency(asset.id)) }}</span>
                </div>
                <div class="ugc-asset-actions">
                  <template v-if="isPurchased(asset.id)">
                    <button class="ugc-btn ugc-btn--primary" :disabled="Number(asset.status) !== ugcAssetStatus.READY || downloadingAssetId === asset.id" @click="downloadAsset(asset.id)">
                      <i class="fal fa-download"></i> Download
                    </button>
                  </template>
                  <template v-else>
                    <button class="ugc-btn ugc-btn--outline" :disabled="Number(asset.status) !== ugcAssetStatus.READY" @click="openPreview(asset)">Preview</button>
                    <button v-if="showBuyButton(asset)" class="ugc-btn ugc-btn--primary" @click="openBuyDialog(asset)">Buy</button>
                    <button v-else-if="isPaymentInProgress(asset.id)" class="ugc-btn ugc-btn--warning" @click="openBuyDialog(asset)">Try again</button>
                  </template>
                </div>
              </div>
            </div>
          </div>
          <div v-else class="ugc-empty">No UGC assets found.</div>
        </div>

        <!-- PURCHASES SECTION -->
        <template v-if="purchaseHistory.length">
          <div class="ugc-section-divider"></div>

          <div class="ugc-section-header">
            <div class="ugc-section-header__left">
              <span class="ugc-section-title">Purchases</span>
              <span class="ugc-section-count">{{ purchaseHistory.length }}</span>
            </div>
          </div>

          <div class="ugc-section-body">
            <div class="ugc-purchase-list">
              <div v-for="item in purchaseHistory" :key="item.purchase.id" class="ugc-purchase-row">

                <!-- Thumb -->
                <div class="ugc-purchase-thumb" :style="item.asset && !item.asset.thumbnail_url ? assetPlaceholderStyle(item.asset.id) : {}">
                  <img v-if="item.asset && item.asset.thumbnail_url" :src="item.asset.thumbnail_url" class="ugc-purchase-thumb-img" alt="" />
                  <i
                    v-else
                    class="fal"
                    :class="item.asset && item.asset.mime_type && item.asset.mime_type.startsWith('video') ? 'fa-play-circle' : 'fa-image'"
                  ></i>
                </div>

                <!-- File info -->
                <div class="ugc-purchase-info">
                  <div class="ugc-purchase-filename">{{ item.asset ? item.asset.original_filename : 'Deleted asset' }}</div>
                  <div class="ugc-purchase-rights">
                    <i class="fal fa-shield-check"></i>
                    {{ item.asset ? formatUsageRights(item.asset.usage_rights) : '-' }}
                    <span v-if="item.asset && item.asset.rights_period" class="ugc-purchase-period">· {{ formatRightsPeriod(item.asset.rights_period) }}</span>
                  </div>
                </div>

                <!-- Influencer avatar -->
                <div class="ugc-purchase-influencer">
                <div class="ugc-avatar" :style="!hasAvatar(item.asset) ? avatarStyle(item.asset ? item.asset.influencer_id : 0) : null">
                  <img v-if="hasAvatar(item.asset)" :src="item.asset.influencer.user.avatar_url" class="ugc-avatar-img" alt="" />
                  <template v-else>{{ avatarInitials(item.asset) }}</template>
                </div>
                  <span class="ugc-purchase-influencer-name">{{ item.asset ? getInfluencerName(item.asset) : '-' }}</span>
                </div>

                <!-- Dates -->
                <div class="ugc-purchase-dates">
                  <div class="ugc-purchase-date-row">
                    <span class="ugc-purchase-date-label">Purchased</span>
                    <span class="ugc-purchase-date-val">{{ formatDate(item.purchase.paid_at || item.purchase.purchased_at) }}</span>
                  </div>
                  <div class="ugc-purchase-date-row">
                    <span class="ugc-purchase-date-label">Expires</span>
                    <span class="ugc-purchase-date-val">{{ item.asset ? formatDate(item.asset.available_until) : '-' }}</span>
                  </div>
                </div>

                <!-- Price -->
                <div class="ugc-purchase-price">
                  {{ formatMinorAmount(item.purchase.total_price, item.purchase.currency) }}
                </div>

                <!-- Status + action -->
                <div class="ugc-purchase-actions">
                  <span class="ugc-pill" :class="'ugc-status--' + purchaseStatusColor(item)">{{ purchaseStatusLabel(item) }}</span>
                  <button class="ugc-btn ugc-btn--primary" :disabled="!item.isAvailable || downloadingAssetId === (item.asset && item.asset.id)" @click="downloadAsset(item.asset.id)">
                    <i class="fal fa-download"></i> Download
                  </button>
                </div>

              </div>
            </div>
          </div>
        </template>

      </div>

      <!-- Purchase Dialog -->
      <v-dialog v-model="showBuyDialog" max-width="580" persistent>
        <div class="ugc-dialog">

          <!-- Header -->
          <div class="ugc-dialog__head">
            <div>
              <div class="ugc-dialog__eyebrow">UGC Purchase</div>
              <div class="ugc-dialog__title">Review your order</div>
            </div>
            <button class="ugc-dialog__close" @click="closeBuyDialog">
              <i class="fal fa-times"></i>
            </button>
          </div>

          <div class="ugc-dialog__body" v-if="selectedAsset">

            <!-- CONFIRM STEP -->
            <template v-if="confirmStep">
              <div class="ugc-dialog__confirm-box">
                <div class="ugc-dialog__confirm-icon">
                  <i class="fal fa-exclamation-triangle"></i>
                </div>
                <div class="ugc-dialog__confirm-title">Are you sure?</div>
                <div class="ugc-dialog__confirm-body">
                  You will be charged
                  <strong>{{ formatMinorAmount(selectedPricing ? selectedPricing.total_price : 0, selectedPricing ? selectedPricing.currency : 'dkk') }}</strong>
                  via your saved
                  <span v-if="savedCard">{{ savedCard.brand }} card (····{{ savedCard.last4 }})</span>
                  <span v-else>payment card</span>.
                </div>
              </div>
            </template>

            <!-- REVIEW STEP -->
            <template v-else>

              <!-- File card -->
              <div class="ugc-dialog__file-card">
                <div class="ugc-dialog__file-icon">
                  <i class="fal fa-photo-video"></i>
                </div>
                <div class="ugc-dialog__file-meta">
                  <div class="ugc-dialog__file-label">Selected asset</div>
                  <div class="ugc-dialog__file-name">{{ selectedAsset.original_filename }}</div>
                </div>
              </div>

              <!-- Rights -->
              <div v-if="selectedAsset.usage_rights && selectedAsset.usage_rights.length" class="ugc-dialog__rights">
                <div class="ugc-dialog__rights-label">Rights included</div>
                <div class="ugc-dialog__rights-value">{{ formatUsageRights(selectedAsset.usage_rights) }}</div>
                <div v-if="selectedAsset.rights_period" class="ugc-dialog__rights-meta">
                  Period: {{ formatRightsPeriod(selectedAsset.rights_period) }}
                </div>
                <div v-if="selectedAsset.extra_terms" class="ugc-dialog__rights-meta">
                  Terms: {{ selectedAsset.extra_terms }}
                </div>
              </div>

              <!-- Pricing -->
              <div v-if="selectedPricing" class="ugc-dialog__pricing">
                <div class="ugc-dialog__pricing-row">
                  <span>Base price</span>
                  <span>{{ formatMinorAmount((selectedPricing.base_price || 0) + (selectedPricing.overhead_price || 0), selectedPricing.currency) }}</span>
                </div>
                <div class="ugc-dialog__pricing-row">
                  <span>Service fee ({{ selectedPricing.commission_percent }}%)</span>
                  <span>{{ formatMinorAmount(selectedPricing.commission_amount, selectedPricing.currency) }}</span>
                </div>
                <div class="ugc-dialog__pricing-divider"></div>
                <div class="ugc-dialog__pricing-total">
                  <div>
                    <div class="ugc-dialog__total-label">Total</div>
                    <div class="ugc-dialog__total-note">
                      <span v-if="hasPaymentMethod && savedCard">Charged to {{ savedCard.brand }} ····{{ savedCard.last4 }}</span>
                      <span v-else>One-time payment via Stripe</span>
                    </div>
                  </div>
                  <div class="ugc-dialog__total-amount">
                    {{ formatMinorAmount(selectedPricing.total_price, selectedPricing.currency) }}
                  </div>
                </div>
              </div>

              <div v-else class="ugc-dialog__loading">
                <i class="fal fa-spinner fa-spin"></i> Loading pricing…
              </div>

              <!-- Notice -->
              <div class="ugc-dialog__notice">
                Once the influencer has uploaded your UGC content, you have 7 days to complete the purchase and download the material from your UGC library. After 7 days, access to the content expires.
              </div>

            </template>
          </div>

          <!-- Footer -->
          <div class="ugc-dialog__foot">
            <button class="ugc-btn ugc-btn--ghost" @click="confirmStep ? (confirmStep = false) : closeBuyDialog()">
              {{ confirmStep ? 'Back' : 'Cancel' }}
            </button>
            <button
              class="ugc-btn ugc-btn--primary"
              :disabled="!selectedPricing || checkoutLoading || directPurchaseLoading"
              @click="confirmBuy"
            >
              <i v-if="checkoutLoading || directPurchaseLoading" class="fal fa-spinner fa-spin"></i>
              <span v-if="confirmStep">Yes, confirm purchase</span>
              <span v-else-if="hasPaymentMethod">Confirm purchase</span>
              <span v-else>Continue to payment</span>
            </button>
          </div>

        </div>
      </v-dialog>

      <!-- Preview dialog -->
      <v-dialog v-model="showPreviewDialog" max-width="900" persistent>
        <v-card>
          <v-card-title class="d-flex justify-space-between align-center">
            <span>{{ previewAsset ? previewAsset.original_filename : 'Preview' }}</span>
            <v-btn icon @click="closePreview"><v-icon>fal fa-times</v-icon></v-btn>
          </v-card-title>
          <v-card-text>
            <div v-if="previewLoading" class="text-center py-8">Loading preview...</div>
            <div v-else-if="previewUrl" class="ugc-preview-wrapper" @click="activatePreviewAudio">
              <video ref="previewVideo" :src="previewUrl" autoplay muted loop playsinline disablepictureinpicture controlslist="nodownload nofullscreen" class="ugc-preview-video" @contextmenu.prevent></video>
              <div class="ugc-preview-watermark">PREVIEW · MAKE INFLUENCE · NOT PURCHASED</div>
              <div v-if="previewMuted" class="ugc-preview-audio-hint">Click to enable audio</div>
            </div>
            <div v-else class="text--secondary">No preview available.</div>
          </v-card-text>
        </v-card>
      </v-dialog>

    </div>
  </subscription-guard>
</template>

<script>
import { mapActions } from "vuex";
import SubscriptionGuard from "@/components/business/pages/SubscriptionGuard.vue";

const UGC_ASSET_STATUS = { PENDING_UPLOAD: 0, READY: 1, FAILED: 2, DELETED: 3 };
const UGC_PURCHASE_STATUS = { PENDING: 0, CHECKOUT_CREATED: 1, PAID: 2, FAILED: 3, REFUNDED: 4 };

const AVATAR_COLORS = [
  ['#fde8e5','#c0392b'],['#e8f0fe','#1a56db'],['#e6f4ea','#137333'],
  ['#fef3cd','#b45309'],['#f0e6ff','#6d28d9'],['#fce4ec','#c2185b'],
  ['#e0f7fa','#00838f'],['#fff3e0','#e65100'],['#f3e5f5','#7b1fa2'],['#e8f5e9','#2e7d32'],
];

export default {
  components: { SubscriptionGuard },
  name: "UgcLibrary",
  data() {
    return {
      loading: false,
      error: "",
      assets: [],
      purchases: [],
      pricingByAssetId: {},
      showBuyDialog: false,
      selectedAsset: null,
      selectedPricing: null,
      checkoutLoading: false,
      downloadingAssetId: null,
      filterStatus: "all",
      search: "",
      filterOptions: [
        { text: "All",                value: "all" },
        { text: "Ready",              value: "ready" },
        { text: "Locked",             value: "locked" },
        { text: "Payment in progress",value: "in_progress" },
        { text: "Purchased",          value: "purchased" },
      ],
      showPreviewDialog: false,
      previewUrl: "",
      previewAsset: null,
      previewLoading: false,
      previewMuted: true,
      ugcAssetStatus: UGC_ASSET_STATUS,
      ugcPurchaseStatus: UGC_PURCHASE_STATUS,
      hasPaymentMethod: false,
      savedCard: null,
      confirmStep: false,
      directPurchaseLoading: false,
      faqs: [
        {
          title: 'What is UGC content?', open: false,
          content: '<p>UGC (User Generated Content) is content created by influencers or creators that businesses can use in their marketing.</p><p class="mb-1">Typically used for:</p><ul><li>Paid ads</li><li>Social media</li><li>Webshops</li><li>Landing pages</li></ul>'
        },
        {
          title: 'When does it make sense?', open: false,
          content: '<p class="mb-1">UGC makes sense when:</p><ul><li>You want content that converts</li><li>You need ad content</li><li>You want to scale paid ads</li><li>You want authentic videos</li></ul>'
        },
        {
          title: 'Rules and responsibility', open: false,
          content: '<ul><li class="mb-2">All amounts are excl. VAT</li><li class="mb-2"><strong>UGC rights are binding</strong><br>Once content is purchased, the selected rights and period apply.</li><li><strong>Make Influence is not a mediator in rights disputes</strong></li></ul>'
        },
        {
          title: 'Recommended approach', open: false,
          content: '<ol><li class="mb-2"><strong>Be clear about usage</strong><br>Describe where the content will be used.</li><li class="mb-2"><strong>Purchase the right rights</strong></li><li><strong>Respect the rights period</strong></li></ol>'
        },
        {
          title: 'Examples of rights', open: false,
          content: '<ul><li>Social media</li><li>Paid ads</li><li>Website / webshop</li><li>Email marketing</li><li>Whitelisting</li><li>Full buyout</li></ul>'
        },
        {
          title: 'Important to understand', open: false,
          content: '<p>UGC is not a guarantee of performance. It is payment for content production, usage rights, and the creator\'s creative work.</p>'
        },
      ],
    };
  },
  computed: {
    filteredAssets() {
      let assets = (this.assets || []).filter(asset => {
        if (asset.deleted_at_storage) return false;
        if (!asset.available_until) return true;
        return new Date(asset.available_until).getTime() > Date.now();
      });
      if (this.search) {
        const q = this.search.toLowerCase();
        assets = assets.filter(a => (a.original_filename || '').toLowerCase().includes(q));
      }
      if (this.filterStatus === 'ready') assets = assets.filter(a => Number(a.status) === this.ugcAssetStatus.READY);
      if (this.filterStatus === 'locked') assets = assets.filter(a => !this.hasAnyPurchase(a.id));
      if (this.filterStatus === 'in_progress') assets = assets.filter(a => this.isPaymentInProgress(a.id));
      if (this.filterStatus === 'purchased') assets = assets.filter(a => this.isPurchased(a.id));
      return assets;
    },
    purchaseHistory() {
      return (this.purchases || []).slice().sort((a, b) => {
        return new Date(b.paid_at || b.purchased_at || b.created_at || 0) - new Date(a.paid_at || a.purchased_at || a.created_at || 0);
      }).map(purchase => {
        const asset = purchase.ugcAsset || null;
        const expiryTimestamp = asset && asset.available_until ? new Date(asset.available_until).getTime() : null;
        const daysLeft = expiryTimestamp !== null ? Math.ceil((expiryTimestamp - Date.now()) / 86400000) : null;
        const isPaid = Number(purchase.status) === this.ugcPurchaseStatus.PAID;
        const isAvailable = Boolean(isPaid && asset && !asset.deleted_at_storage && Number(asset.status) === this.ugcAssetStatus.READY && (expiryTimestamp === null || expiryTimestamp > Date.now()));
        return { purchase, asset, isAvailable, expiringSoon: isAvailable && daysLeft !== null && daysLeft <= 2 };
      });
    },
  },
  created() {
    this.loadData();
    this.checkPaymentMethod();
  },
  methods: {
    ...mapActions("business", [
      "getBusinessUgcAssets", "requestUgcDownloadUrl", "getUgcAssetPurchases",
      "getUgcAssetPricingPreview", "createUgcAssetCheckoutSession",
      "previewBusinessUgcAsset", "businessHasPaymentMethod", "purchaseUgcAssetDirectly",
    ]),
    async loadData() {
      this.loading = true; this.error = "";
      try {
        const [assets, purchases] = await Promise.all([this.getBusinessUgcAssets(), this.getUgcAssetPurchases()]);
        this.assets = assets || []; this.purchases = purchases || [];
        await this.loadPricingForAssets();
      } catch (e) { this.error = e?.message || "Could not load UGC assets."; }
      finally { this.loading = false; }
    },
    async loadPricingForAssets() {
      const map = {};
      await Promise.all((this.assets || []).map(async a => {
        try { map[String(a.id)] = await this.getUgcAssetPricingPreview({ ugc_asset_id: a.id }); }
        catch { map[String(a.id)] = null; }
      }));
      this.pricingByAssetId = map;
    },
    hasAnyPurchase(id) { return this.purchases.some(p => String(p.ugc_asset_id) === String(id)); },
    getPurchase(id) { return this.purchases.find(p => p && String(p.ugc_asset_id) === String(id)) || null; },
    getPurchaseStatus(id) { const p = this.getPurchase(id); return p ? Number(p.status) : null; },
    isPaymentInProgress(id) { const s = this.getPurchaseStatus(id); return s === this.ugcPurchaseStatus.PENDING || s === this.ugcPurchaseStatus.CHECKOUT_CREATED; },
    isPurchased(id) { return this.purchases.some(p => String(p.ugc_asset_id) === String(id) && Number(p.status) === this.ugcPurchaseStatus.PAID); },
    getStateLabel(asset) {
      if (this.isPurchased(asset.id)) return 'Purchased';
      if (Number(asset.status) === this.ugcAssetStatus.READY && this.isPaymentInProgress(asset.id)) return 'Payment incomplete';
      if (Number(asset.status) === this.ugcAssetStatus.READY) return 'Ready';
      if (Number(asset.status) === this.ugcAssetStatus.PENDING_UPLOAD) return 'Pending upload';
      return 'Locked';
    },
    getStateColor(asset) {
      if (this.isPurchased(asset.id)) return 'success';
      if (Number(asset.status) === this.ugcAssetStatus.READY && this.isPaymentInProgress(asset.id)) return 'warning';
      if (Number(asset.status) === this.ugcAssetStatus.READY) return 'info';
      return 'grey';
    },
    purchaseStatusLabel(item) {
      const s = Number(item.purchase.status);
      if (s === this.ugcPurchaseStatus.PAID) return item.isAvailable ? (item.expiringSoon ? 'Expiring soon' : 'Ready to download') : 'Expired';
      if (s === this.ugcPurchaseStatus.REFUNDED) return 'Refunded';
      if (s === this.ugcPurchaseStatus.FAILED) return 'Payment failed';
      return 'Awaiting payment';
    },
    purchaseStatusColor(item) {
      const s = Number(item.purchase.status);
      if (s === this.ugcPurchaseStatus.PAID) return item.isAvailable ? (item.expiringSoon ? 'warning' : 'success') : 'grey';
      if (s === this.ugcPurchaseStatus.REFUNDED) return 'grey';
      if (s === this.ugcPurchaseStatus.FAILED) return 'error';
      return 'warning';
    },
    getInfluencerName(asset) {
      if (asset.influencer?.user?.first_name || asset.influencer?.user?.last_name)
        return `${asset.influencer.user.first_name || ''} ${asset.influencer.user.last_name || ''}`.trim();
      return 'Influencer #' + asset.influencer_id;
    },
    hasAvatar(asset) {
      return !!(asset && asset.influencer && asset.influencer.user && asset.influencer.user.avatar_url);
    },
    avatarInitials(asset) {
      if (!asset) return '?';
      if (asset.influencer?.user?.first_name || asset.influencer?.user?.last_name) {
        const f = (asset.influencer.user.first_name || '')[0] || '';
        const l = (asset.influencer.user.last_name || '')[0] || '';
        return (f + l).toUpperCase() || '?';
      }
      return '#' + asset.influencer_id;
    },
    avatarStyle(influencerId) {
      const idx = (influencerId || 0) % AVATAR_COLORS.length;
      return { background: AVATAR_COLORS[idx][0], color: AVATAR_COLORS[idx][1] };
    },
    getDisplayedPrice(id) {
      const p = this.getPurchase(id);
      if (p?.total_price != null) return p.total_price;
      return this.pricingByAssetId[String(id)]?.total_price ?? null;
    },
    getDisplayedCurrency(id) {
      const p = this.getPurchase(id);
      return p?.currency || this.pricingByAssetId[String(id)]?.currency || 'dkk';
    },
    showBuyButton(asset) { return !this.isPurchased(asset.id) && !this.isPaymentInProgress(asset.id); },
    openBuyDialog(asset) {
      this.error = ''; this.selectedAsset = asset;
      this.selectedPricing = this.pricingByAssetId[String(asset.id)] || null;
      this.showBuyDialog = true;
      if (!this.selectedPricing) this.loadSelectedPricing(asset.id);
    },
    async loadSelectedPricing(id) {
      try { this.selectedPricing = await this.getUgcAssetPricingPreview({ ugc_asset_id: id }); this.$set(this.pricingByAssetId, String(id), this.selectedPricing); }
      catch (e) { this.error = e?.message || 'Could not load pricing preview.'; }
    },
    closeBuyDialog() { this.showBuyDialog = false; this.selectedAsset = null; this.selectedPricing = null; this.checkoutLoading = false; this.confirmStep = false; this.directPurchaseLoading = false; },
    async confirmBuy() {
      if (!this.selectedAsset) return;
      if (this.hasPaymentMethod && !this.confirmStep) { this.confirmStep = true; return; }
      if (this.hasPaymentMethod) {
        this.directPurchaseLoading = true; this.error = '';
        try { const r = await this.purchaseUgcAssetDirectly({ ugc_asset_id: this.selectedAsset.id }); if (r.success) { this.closeBuyDialog(); await this.loadData(); } else { this.error = r.error || 'Payment failed.'; this.confirmStep = false; } }
        catch (e) { this.error = e?.message || 'Payment failed.'; this.confirmStep = false; }
        finally { this.directPurchaseLoading = false; }
        return;
      }
      this.checkoutLoading = true; this.error = '';
      try { const r = await this.createUgcAssetCheckoutSession({ ugc_asset_id: this.selectedAsset.id }); window.location.href = r.checkout_url; }
      catch (e) { this.error = e?.message || 'Could not start checkout.'; }
      finally { this.checkoutLoading = false; }
    },
    async openPreview(asset) {
      this.previewLoading = true; this.previewUrl = ''; this.previewAsset = asset; this.previewMuted = true; this.showPreviewDialog = true; this.error = '';
      try {
        const r = await this.previewBusinessUgcAsset({ ugc_asset_id: asset.id });
        this.previewUrl = r.url;
        this.$nextTick(() => { const v = this.$refs.previewVideo; if (v) { v.muted = true; v.play().catch(() => {}); } });
      } catch (e) { this.error = e?.message || 'Could not load preview.'; this.showPreviewDialog = false; }
      finally { this.previewLoading = false; }
    },
    activatePreviewAudio() {
      const v = this.$refs.previewVideo; if (!v) return;
      if (this.previewMuted) { v.muted = false; this.previewMuted = false; v.play().catch(() => {}); return; }
      v.paused ? v.play().catch(() => {}) : v.pause();
    },
    closePreview() { const v = this.$refs.previewVideo; if (v) v.pause(); this.showPreviewDialog = false; this.previewUrl = ''; this.previewAsset = null; this.previewMuted = true; },
    async downloadAsset(id) {
      this.error = ''; this.downloadingAssetId = id;
      try { const r = await this.requestUgcDownloadUrl({ ugc_asset_id: id }); window.open(r.url, '_blank'); }
      catch (e) { this.error = e?.message || 'Could not generate download link.'; }
      finally { this.downloadingAssetId = null; }
    },
    formatDate(v) { if (!v) return '-'; const d = new Date(v); if (isNaN(d)) return v; return `${String(d.getDate()).padStart(2,'0')}/${String(d.getMonth()+1).padStart(2,'0')}/${d.getFullYear()}`; },
    formatBytes(v) { if (!v) return '-'; const s = ['B','KB','MB','GB']; const i = Math.floor(Math.log(v)/Math.log(1024)); return `${(v/Math.pow(1024,i)).toFixed(1)} ${s[i]}`; },
    formatUsageRights(v) {
      if (!v?.length) return '-';
      const m = {
        social_media: 'Social media', paid_ads: 'Paid ads', website: 'Website',
        email_marketing: 'Email marketing', landing_pages: 'Landing pages', print_outdoor: 'Print/outdoor'
      };
      return v.map(x => m[x] || x).join(', ');
    },
    formatRightsPeriod(v) {
      const m = { '30': '30 days', '90': '90 days', '180': '6 months', '365': '12 months', 'unlimited': 'Unlimited' };
      return m[v] || v || '-';
    },
    formatMinorAmount(v, currency='dkk') { if (v==null) return '-'; return `${(v/100).toFixed(2)} ${String(currency).toUpperCase()}`; },
    async checkPaymentMethod() { try { const r = await this.businessHasPaymentMethod(); this.hasPaymentMethod = r.has_payment_method; this.savedCard = r.has_payment_method ? { last4: r.last4, brand: r.brand } : null; } catch { this.hasPaymentMethod = false; this.savedCard = null; } },
    assetPlaceholderStyle(id) {
      const gradients = ['linear-gradient(135deg,#f5c6c0,#e8a89e)','linear-gradient(135deg,#c0d8f5,#9ec0e8)','linear-gradient(135deg,#c0f5d0,#9ee8b4)','linear-gradient(135deg,#f5ecc0,#e8d49e)','linear-gradient(135deg,#e0c0f5,#c89ee8)','linear-gradient(135deg,#f5d0c0,#e8b49e)'];
      return { background: gradients[(id || 0) % gradients.length] };
    },
    toggleFaq(index) {
      this.faqs = this.faqs.map((f, i) => ({ ...f, open: i === index ? !f.open : false }));
    },
  },
};
</script>

<style scoped>
.ugc-page { max-width: 1160px; margin: 0 auto; padding: 0; }

.ugc-header { margin-bottom: 20px; }
.ugc-title { font-size: 22px; font-weight: 700; color: var(--ink-1, #1a1a1a); display: flex; align-items: center; gap: 8px; margin: 0 0 4px; }
.ugc-beta { font-size: 10px; font-weight: 700; background: #fceae8; color: #e05540; border: 1px solid #f0d0ca; border-radius: 6px; padding: 1px 7px; }
.ugc-subtitle { font-size: 13px; color: var(--ink-3, #888); margin: 0; }

/* FAQ grid */
.ugc-faq-grid { display: grid; grid-template-columns: repeat(3, 1fr); gap: 10px; margin-bottom: 20px; align-items: start; }
@media (max-width: 900px) { .ugc-faq-grid { grid-template-columns: repeat(2, 1fr); } }
@media (max-width: 600px) { .ugc-faq-grid { grid-template-columns: 1fr; } }
.ugc-faq-card { border: 1px solid var(--line, #e8e8e8); border-radius: 10px; overflow: hidden; background: var(--surface, #fff); }
.ugc-faq-header { width: 100%; display: flex; align-items: center; justify-content: space-between; padding: 14px 16px; font-size: 13px; font-weight: 600; color: var(--ink-1, #1a1a1a); background: none; border: none; cursor: pointer; gap: 8px; text-align: left; }
.ugc-faq-body { padding: 0 16px 14px; font-size: 13px; color: var(--ink-2, #555); line-height: 1.6; }
.ugc-faq-body ul, .ugc-faq-body ol { padding-left: 18px; margin: 6px 0 0; }
.ugc-faq-body li { margin-bottom: 4px; }
.ugc-faq-body p { margin: 0 0 8px; }

/* Main card */
.ugc-main-card { background: var(--surface, #fff); border: 1px solid var(--line, #e8e8e8); border-radius: 12px; }

/* Section header — single row */
.ugc-section-header { display: flex; align-items: center; justify-content: space-between; padding: 14px 20px; gap: 12px; border-bottom: 1px solid var(--line, #e8e8e8); }
.ugc-section-header__left { display: flex; align-items: center; gap: 8px; flex-shrink: 0; }
.ugc-section-header__right { display: flex; align-items: center; gap: 8px; flex-shrink: 0; justify-content: flex-end; }
.ugc-section-title { font-size: 13px; font-weight: 700; color: var(--ink-1, #1a1a1a); white-space: nowrap; }
.ugc-section-count { font-size: 11px; font-weight: 700; background: var(--surface-2, #f3f3f3); color: var(--ink-3, #888); border-radius: 20px; padding: 1px 8px; }
.ugc-section-divider { height: 1px; background: var(--line, #e8e8e8); }
.ugc-section-body { padding: 16px 20px 20px; }

/* Search / filter inline */
.ugc-search-wrap { display: flex; align-items: center; gap: 6px; border: 1px solid var(--line, #e8e8e8); border-radius: 8px; padding: 0 10px; height: 34px; background: #fff; width: 200px; flex-shrink: 0; }
.ugc-search-icon { color: var(--ink-4, #bbb); font-size: 12px; flex-shrink: 0; }
.ugc-search-input { border: none; outline: none; background: transparent; font-size: 12.5px; color: var(--ink-1, #1a1a1a); width: 100%; min-width: 0; }
.ugc-search-input::placeholder { color: var(--ink-4, #bbb); }
.ugc-select { border: 1px solid var(--line, #e8e8e8); border-radius: 8px; padding: 0 8px; height: 34px; font-size: 12.5px; color: var(--ink-1, #1a1a1a); background: #fff; outline: none; cursor: pointer; flex-shrink: 0; }
.ugc-refresh-btn { display: flex; align-items: center; gap: 5px; font-size: 12px; font-weight: 600; color: var(--ink-2, #555); background: #fff; border: 1px solid var(--line, #e8e8e8); border-radius: 7px; padding: 0 12px; height: 34px; cursor: pointer; white-space: nowrap; flex-shrink: 0; }

.ugc-error { background: #fef2f2; color: #c0392b; border: 1px solid #f5c6c0; border-radius: 8px; padding: 10px 14px; margin: 0 20px 16px; font-size: 13px; }

/* Asset grid */
.ugc-grid { display: grid; grid-template-columns: repeat(4, 1fr); gap: 14px; }
@media (max-width: 1000px) { .ugc-grid { grid-template-columns: repeat(3, 1fr); } }
@media (max-width: 700px) { .ugc-grid { grid-template-columns: repeat(2, 1fr); } }

.ugc-asset-card { border: 1px solid var(--line, #e8e8e8); border-radius: 10px; overflow: hidden; background: var(--surface, #fff); }
.ugc-asset-thumb { aspect-ratio: 1; display: flex; align-items: center; justify-content: center; position: relative; }
.ugc-asset-thumb i { font-size: 28px; color: rgba(255,255,255,0.7); }
.ugc-asset-thumb-img {
  position: absolute;
  inset: 0;
  width: 100%;
  height: 100%;
  object-fit: cover;
}
.ugc-asset-play-icon {
  position: relative;
  z-index: 1;
  font-size: 28px;
  color: rgba(255, 255, 255, 0.92);
  text-shadow: 0 2px 10px rgba(0, 0, 0, 0.45);
}
.ugc-asset-body { padding: 10px 12px; }
.ugc-asset-filename { font-size: 12px; font-weight: 600; color: var(--ink-1, #1a1a1a); white-space: nowrap; overflow: hidden; text-overflow: ellipsis; margin-bottom: 4px; }
.ugc-asset-meta { display: flex; justify-content: space-between; font-size: 11px; color: var(--ink-3, #888); margin-bottom: 8px; }
.ugc-asset-price { font-weight: 700; color: var(--ink-1, #1a1a1a); }
.ugc-asset-actions { display: flex; gap: 6px; flex-wrap: wrap; }

/* Status badge */
.ugc-asset-status-badge { font-size: 10px; font-weight: 700; border-radius: 6px; padding: 2px 7px; position: absolute; top: 8px; left: 8px; }
.ugc-pill { font-size: 10.5px; font-weight: 700; border-radius: 6px; padding: 3px 9px; display: inline-block; white-space: nowrap; }
.ugc-status--success { background: #d4edda; color: #155724; }
.ugc-status--info { background: #d1ecf1; color: #0c5460; }
.ugc-status--warning { background: #fff3cd; color: #856404; }
.ugc-status--error { background: #f8d7da; color: #721c24; }
.ugc-status--grey { background: #e9ecef; color: #6c757d; }

/* Buttons */
.ugc-btn { font-size: 11.5px; font-weight: 600; border-radius: 7px; padding: 5px 10px; border: none; cursor: pointer; display: inline-flex; align-items: center; gap: 4px; }
.ugc-btn:disabled { opacity: 0.5; cursor: not-allowed; }
.ugc-btn--primary { background: #e05540; color: #fff; }
.ugc-btn--outline { background: transparent; border: 1px solid var(--line, #e8e8e8); color: var(--ink-2, #444); }
.ugc-btn--warning { background: #f39c12; color: #fff; }
.ugc-btn--ghost { background: var(--surface-2, #f7f7f7); border: 1px solid var(--line, #e8e8e8); color: var(--ink-2, #555); }

/* Purchase list */
.ugc-purchase-list { display: flex; flex-direction: column; gap: 10px; }
.ugc-purchase-row {
  display: flex;
  align-items: center;
  gap: 14px;
  padding: 14px 16px;
  border: 1px solid var(--line, #e8e8e8);
  border-radius: 10px;
  background: var(--surface, #fff);
  transition: box-shadow 0.15s;
}
.ugc-purchase-row:hover { box-shadow: 0 2px 10px rgba(0,0,0,0.06); }

/* Thumbnail */
.ugc-purchase-thumb {
  width: 52px;
  height: 52px;
  border-radius: 8px;
  flex-shrink: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  overflow: hidden;
}
.ugc-purchase-thumb i { font-size: 18px; color: rgba(255,255,255,0.75); }

.ugc-purchase-thumb-img {
  width: 100%;
  height: 100%;
  object-fit: cover;
}

/* File info */
.ugc-purchase-info { flex: 2; min-width: 0; }
.ugc-purchase-filename { font-size: 13px; font-weight: 600; color: var(--ink-1, #1a1a1a); white-space: nowrap; overflow: hidden; text-overflow: ellipsis; margin-bottom: 4px; }
.ugc-purchase-rights { font-size: 11.5px; color: var(--ink-3, #888); display: flex; align-items: center; gap: 5px; flex-wrap: wrap; }
.ugc-purchase-rights i { color: #e05540; font-size: 11px; }
.ugc-purchase-period { color: var(--ink-4, #bbb); }

/* Influencer */
.ugc-purchase-influencer { display: flex; align-items: center; gap: 9px; flex: 1.2; min-width: 0; }
.ugc-avatar {
  width: 34px;
  height: 34px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 11px;
  font-weight: 700;
  flex-shrink: 0;
  overflow: hidden;
}
.ugc-avatar-img {
  width: 100%;
  height: 100%;
  object-fit: cover;
}
.ugc-purchase-influencer-name { font-size: 12.5px; font-weight: 500; color: var(--ink-1, #1a1a1a); white-space: nowrap; overflow: hidden; text-overflow: ellipsis; }

/* Dates */
.ugc-purchase-dates { flex: 1; display: flex; flex-direction: column; gap: 4px; min-width: 0; }
.ugc-purchase-date-row { display: flex; gap: 6px; align-items: baseline; }
.ugc-purchase-date-label { font-size: 10px; font-weight: 700; text-transform: uppercase; letter-spacing: 0.4px; color: var(--ink-4, #bbb); width: 46px; flex-shrink: 0; }
.ugc-purchase-date-val { font-size: 12px; color: var(--ink-2, #555); }

/* Price */
.ugc-purchase-price { font-size: 13px; font-weight: 700; color: var(--ink-1, #1a1a1a); white-space: nowrap; flex-shrink: 0; }

/* Actions */
.ugc-purchase-actions { display: flex; align-items: center; gap: 8px; flex-shrink: 0; }

.ugc-empty { color: var(--ink-3, #888); font-size: 13px; font-style: italic; padding: 20px 0; }

/* Preview dialog */
.ugc-preview-wrapper { position: relative; width: 100%; overflow: hidden; border-radius: 8px; }
.ugc-preview-video { width: 100%; max-height: 70vh; display: block; }
.ugc-preview-audio-hint { position: absolute; left: 50%; bottom: 24px; transform: translateX(-50%); background: rgba(0,0,0,0.65); color: white; padding: 10px 16px; border-radius: 999px; font-size: 14px; font-weight: 600; pointer-events: none; }
.ugc-preview-watermark { position: absolute; inset: 0; display: flex; align-items: center; justify-content: center; text-align: center; pointer-events: none; font-size: 28px; font-weight: 700; letter-spacing: 2px; color: rgba(255,255,255,0.75); text-shadow: 0 2px 8px rgba(0,0,0,0.45); padding: 24px; }

/* Buy dialog */
.ugc-buy-dialog { border-radius: 16px; overflow: hidden; }
.ugc-buy-dialog__title { display: flex; align-items: flex-start; justify-content: space-between; padding: 24px 24px 8px 24px; }
.ugc-buy-dialog__eyebrow { font-size: 12px; font-weight: 700; letter-spacing: 0.08em; text-transform: uppercase; color: #7b3fe4; margin-bottom: 4px; }
.ugc-buy-dialog__headline { font-size: 24px; font-weight: 700; line-height: 1.2; color: #1f1f1f; }
.ugc-buy-dialog__content { padding: 8px 24px 24px 24px; }
.ugc-buy-dialog__file-card { display: flex; align-items: center; gap: 16px; background: #f8f7fc; border: 1px solid #ebe7f7; border-radius: 14px; padding: 16px; margin-bottom: 20px; }
.ugc-buy-dialog__file-icon { width: 48px; height: 48px; border-radius: 12px; background: white; display: flex; align-items: center; justify-content: center; flex-shrink: 0; }
.ugc-buy-dialog__file-label { font-size: 12px; color: #6f6f6f; margin-bottom: 4px; }
.ugc-buy-dialog__file-name { font-size: 15px; font-weight: 600; color: #1f1f1f; word-break: break-word; }
.ugc-buy-dialog__pricing { background: #fff; border: 1px solid #ececec; border-radius: 14px; padding: 16px; }
.ugc-buy-dialog__row { display: flex; align-items: center; justify-content: space-between; gap: 16px; padding: 10px 0; font-size: 14px; color: #424242; }
.ugc-buy-dialog__divider { height: 1px; background: #ececec; margin: 8px 0 12px; }
.ugc-buy-dialog__total { display: flex; align-items: center; justify-content: space-between; gap: 16px; }
.ugc-buy-dialog__total-label { font-size: 14px; font-weight: 700; color: #1f1f1f; }
.ugc-buy-dialog__total-note { font-size: 12px; color: #7a7a7a; margin-top: 4px; }
.ugc-buy-dialog__total-amount { font-size: 24px; font-weight: 800; color: #1f1f1f; white-space: nowrap; }
.ugc-buy-dialog__notice { margin-top: 16px; font-size: 13px; line-height: 1.5; color: #666; }
.ugc-buy-dialog__loading { font-size: 14px; color: #666; padding: 12px 0; }
.ugc-buy-dialog__actions { display: flex; justify-content: flex-end; gap: 12px; padding: 0 24px 24px 24px; }
.ugc-buy-dialog__cta { min-width: 180px; }
.ugc-buy-dialog__confirm-box { display: flex; flex-direction: column; align-items: center; text-align: center; padding: 24px 16px; }
.ugc-buy-dialog__confirm-title { font-size: 20px; font-weight: 700; color: #1f1f1f; margin-bottom: 12px; }
.ugc-buy-dialog__confirm-body { font-size: 15px; color: #424242; line-height: 1.6; }

/* ── Purchase Dialog ── */
.ugc-dialog {
  background: var(--surface, #fff);
  border-radius: 14px;
  overflow: hidden;
  display: flex;
  flex-direction: column;
}
.ugc-dialog__head {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  padding: 22px 24px 16px;
  border-bottom: 1px solid var(--line, #ebebeb);
}
.ugc-dialog__eyebrow {
  font-size: 10.5px;
  font-weight: 700;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  color: var(--accent, #e05540);
  margin-bottom: 4px;
}
.ugc-dialog__title {
  font-size: 20px;
  font-weight: 700;
  color: var(--ink-1, #1a1a1a);
}
.ugc-dialog__close {
  background: none;
  border: none;
  cursor: pointer;
  color: var(--ink-3, #888);
  font-size: 18px;
  padding: 2px 4px;
  line-height: 1;
  margin-top: 2px;
}
.ugc-dialog__close:hover { color: var(--ink-1, #1a1a1a); }
.ugc-dialog__body {
  padding: 20px 24px;
  display: flex;
  flex-direction: column;
  gap: 16px;
}
.ugc-dialog__foot {
  padding: 16px 24px;
  border-top: 1px solid var(--line, #ebebeb);
  display: flex;
  justify-content: flex-end;
  gap: 10px;
}

/* File card */
.ugc-dialog__file-card {
  display: flex;
  align-items: center;
  gap: 14px;
  background: var(--surface-2, #f7f7f7);
  border: 1px solid var(--line, #ebebeb);
  border-radius: 10px;
  padding: 14px 16px;
}
.ugc-dialog__file-icon {
  width: 40px;
  height: 40px;
  border-radius: 8px;
  background: var(--surface, #fff);
  border: 1px solid var(--line, #ebebeb);
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 18px;
  color: var(--ink-3, #888);
  flex-shrink: 0;
}
.ugc-dialog__file-label {
  font-size: 11px;
  color: var(--ink-3, #888);
  margin-bottom: 2px;
}
.ugc-dialog__file-name {
  font-size: 14px;
  font-weight: 600;
  color: var(--ink-1, #1a1a1a);
}

/* Rights */
.ugc-dialog__rights {
  display: flex;
  flex-direction: column;
  gap: 3px;
}
.ugc-dialog__rights-label {
  font-size: 11px;
  font-weight: 600;
  color: var(--ink-3, #888);
  text-transform: uppercase;
  letter-spacing: 0.05em;
}
.ugc-dialog__rights-value {
  font-size: 13.5px;
  color: var(--ink-1, #1a1a1a);
}
.ugc-dialog__rights-meta {
  font-size: 12px;
  color: var(--ink-3, #888);
}

/* Pricing */
.ugc-dialog__pricing {
  background: var(--surface-2, #f7f7f7);
  border: 1px solid var(--line, #ebebeb);
  border-radius: 10px;
  padding: 16px;
  display: flex;
  flex-direction: column;
  gap: 10px;
}
.ugc-dialog__pricing-row {
  display: flex;
  justify-content: space-between;
  font-size: 13.5px;
  color: var(--ink-2, #444);
}
.ugc-dialog__pricing-divider {
  height: 1px;
  background: var(--line, #ebebeb);
}
.ugc-dialog__pricing-total {
  display: flex;
  justify-content: space-between;
  align-items: center;
}
.ugc-dialog__total-label {
  font-size: 14px;
  font-weight: 700;
  color: var(--ink-1, #1a1a1a);
}
.ugc-dialog__total-note {
  font-size: 11.5px;
  color: var(--ink-3, #888);
  margin-top: 2px;
}
.ugc-dialog__total-amount {
  font-size: 22px;
  font-weight: 700;
  color: var(--ink-1, #1a1a1a);
}

/* Notice */
.ugc-dialog__notice {
  font-size: 12px;
  color: var(--ink-3, #888);
  line-height: 1.6;
}

/* Confirm box */
.ugc-dialog__confirm-box {
  text-align: center;
  padding: 24px 16px;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 10px;
}
.ugc-dialog__confirm-icon {
  width: 48px;
  height: 48px;
  border-radius: 50%;
  background: #fef3cd;
  color: #b45309;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 20px;
}
.ugc-dialog__confirm-title {
  font-size: 17px;
  font-weight: 700;
  color: var(--ink-1, #1a1a1a);
}
.ugc-dialog__confirm-body {
  font-size: 13.5px;
  color: var(--ink-2, #444);
  line-height: 1.6;
}

/* Loading */
.ugc-dialog__loading {
  font-size: 13px;
  color: var(--ink-3, #888);
  padding: 16px 0;
}

@media (max-width: 700px) {
  .ugc-section-header {
    flex-wrap: wrap;
  }
  .ugc-section-header__right {
    flex-wrap: wrap;
    width: 100%;
  }
  .ugc-search-wrap {
    width: 100%;
  }
  .ugc-select {
    flex: 1;
  }
  .ugc-refresh-btn {
    flex: 1;
    justify-content: center;
  }
}

@media (max-width: 700px) {
  .ugc-purchase-row {
    flex-wrap: wrap;
    row-gap: 10px;
    justify-content: space-between;
  }
  .ugc-purchase-influencer {
    flex-basis: 100%;
  }
  .ugc-purchase-dates {
    flex-basis: 100%;
  }
}
</style>